import React, { useState, useEffect } from "react";
import { Button } from "../../button";
import { Popover, PopoverContent, PopoverTrigger } from "../../popover";
import { X, Clock } from "lucide-react";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import { Input } from "../../input";
import { TimePickerV2 } from "../../time-picker-v2";
import dayjs from "dayjs";
import { formatToTimeString } from "../../../lib/date-time";

interface TimeRangeFilterProps {
  filterState: {
    filterKey: string;
    filter: any;
    setFilter: (filters: any) => void;
  };
  title: string;
  description?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  showTrigger?: boolean;
}

const PopoverTimeRangeFilterV2Component: React.FC<TimeRangeFilterProps> = ({
  filterState,
  title,
  description,
  isOpen,
  onOpenChange,
  showTrigger = true,
}) => {
  const currentFilter = filterState.filter[filterState.filterKey] || { from: "", to: "" };
  const [localFromTime, setLocalFromTime] = useState(currentFilter.from || "");
  const [localToTime, setLocalToTime] = useState(currentFilter.to || "");

  // Update local state when filter changes
  useEffect(() => {
    const filter = filterState.filter[filterState.filterKey] || { from: "", to: "" };
    setLocalFromTime(filter.from || "");
    setLocalToTime(filter.to || "");
  }, [filterState.filter, filterState.filterKey]);

  const handleFromTimeChange = (value: string) => {
    setLocalFromTime(value);
    filterState.setFilter({
      ...filterState.filter,
      [filterState.filterKey]: {
        from: value,
        to: localToTime,
      },
    });
  };

  const handleToTimeChange = (value: string) => {
    setLocalToTime(value);
    filterState.setFilter({
      ...filterState.filter,
      [filterState.filterKey]: {
        from: localFromTime,
        to: value,
      },
    });
  };

  const handleClear = () => {
    setLocalFromTime("");
    setLocalToTime("");
    filterState.setFilter({
      ...filterState.filter,
      [filterState.filterKey]: {
        from: "",
        to: "",
      },
    });
  };

  const hasActiveFilter = localFromTime || localToTime;

  return (
    <Popover
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <PopoverTrigger asChild>
        {showTrigger ? (
          <img
            className="ml-2 w-3 cursor-pointer"
            src={MagnifyingGlass}
            alt="Search"
          />
        ) : (
          <button
            type="button"
            className="sr-only"
            aria-hidden="true"
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-50 p-4"
        align="center"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">{title}</h4>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                From Time
              </label>
              <div className="relative">
                <TimePickerV2
                  value={formatToTimeString(new Date(localFromTime))}
                  onChange={(time) => {
                    if (!time || !dayjs(time, "HH:mm:ss", true).isValid()) return;

                    const localDateTime = dayjs(`${dayjs().format("YYYY-MM-DD")}T${time}`).format(
                      "YYYY-MM-DDTHH:mm:ssZ",
                    );
                    handleFromTimeChange(localDateTime);
                  }}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                To Time
              </label>
              <div className="relative">
                <TimePickerV2
                  value={formatToTimeString(new Date(localToTime))}
                  onChange={(time) => {
                    if (!time || !dayjs(time, "HH:mm:ss", true).isValid()) return;

                    const localDateTime = dayjs(`${dayjs().format("YYYY-MM-DD")}T${time}`).format(
                      "YYYY-MM-DDTHH:mm:ssZ",
                    );
                    handleToTimeChange(localDateTime);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PopoverTimeRangeFilterV2Component;
